// apps/roleforge-app/src/components/audit/AssetsColumn.tsx
//
// Right column. Lists asset cards (Operating Model, Opportunity Matrix, ROI,
// per-opportunity deep dives, Skill Pack) as the underlying state is
// populated by the SSE stream. Clicking a card opens the asset in the middle
// column via `onSelectAsset`.

import { Layers } from 'lucide-react';
import { AssetCard } from './AssetCard';
import type { OperatingModelState } from '../../lib/use-operating-model-state';

export type AssetId =
  | 'operating-model'
  | 'opportunity-matrix'
  | 'roi-summary'
  | 'skill-pack'
  | `opportunity:${string}`
  | `voice:${string}`;

interface Props {
  state: OperatingModelState;
  activeAssetId: AssetId | null;
  onSelectAsset: (id: AssetId) => void;
}

function formatUsd(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}k`;
  return `$${amount}`;
}

export function AssetsColumn({ state, activeAssetId, onSelectAsset }: Props) {
  const { engines, painPoints, opportunities, matrixAssembled, totalSavings } = state;

  const hasAnything = engines.length > 0 || painPoints.length > 0 || opportunities.length > 0;

  if (!hasAnything) {
    return (
      <aside
        data-testid="assets-column"
        className="flex h-full flex-col border-l border-line bg-paper"
      >
        <div className="px-4 pt-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-graphite">Assets</h2>
        </div>
        <div
          data-testid="assets-empty-state"
          className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 pb-10 text-center"
        >
          <div
            aria-hidden="true"
            className="mb-5 flex h-20 w-20 items-center justify-center rounded-full border border-dashed border-line text-graphite"
          >
            <Layers className="h-8 w-8" strokeWidth={1.5} />
          </div>
          <p className="max-w-[14rem] text-sm leading-relaxed text-graphite">
            Assets will appear<br />here as we talk.
          </p>
        </div>
      </aside>
    );
  }

  const topOpportunities = [...opportunities]
    .sort((a, b) => (b.roi?.annual_savings ?? 0) - (a.roi?.annual_savings ?? 0))
    .slice(0, 3);

  return (
    <aside
      data-testid="assets-column"
      className="flex h-full flex-col gap-3 overflow-y-auto border-l border-line bg-paper p-4"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-graphite">Assets</h2>

      {engines.length > 0 && (
        <AssetCard
          assetId="operating-model"
          title="AI Workforce Blueprint"
          subtitle={`${engines.length} engine${engines.length === 1 ? '' : 's'}`}
          active={activeAssetId === 'operating-model'}
          onClick={() => onSelectAsset('operating-model')}
        />
      )}

      {matrixAssembled && (
        <AssetCard
          assetId="opportunity-matrix"
          title="Opportunity Matrix"
          subtitle={`${opportunities.length} opportunit${opportunities.length === 1 ? 'y' : 'ies'}`}
          active={activeAssetId === 'opportunity-matrix'}
          onClick={() => onSelectAsset('opportunity-matrix')}
        />
      )}

      {painPoints.length > 0 && (
        <section className="mt-2 flex flex-col gap-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-graphite">
            Discovery signals
          </h3>
          <div className="rounded-lg border border-line bg-card p-3">
            <p className="text-sm font-semibold text-ink">
              {painPoints.length} pain point{painPoints.length === 1 ? '' : 's'} recorded
            </p>
            <ul className="mt-2 flex max-h-36 flex-col gap-2 overflow-y-auto pr-1">
              {painPoints.slice(-4).map((painPoint) => (
                <li
                  key={painPoint.id}
                  className="border-l-2 border-emerald-500/50 pl-2 text-xs leading-snug text-graphite"
                >
                  {painPoint.summary}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {totalSavings > 0 && (
        <AssetCard
          assetId="roi-summary"
          title="ROI Summary"
          subtitle="Estimated annual savings"
          metric={formatUsd(totalSavings)}
          active={activeAssetId === 'roi-summary'}
          onClick={() => onSelectAsset('roi-summary')}
        />
      )}

      {topOpportunities.length > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-graphite">
            Top opportunities
          </h3>
          {topOpportunities.map((o) => {
            const id: AssetId = `opportunity:${o.opportunity_id}`;
            return (
              <AssetCard
                key={o.opportunity_id}
                assetId={`opportunity-${o.opportunity_id}`}
                title={o.title}
                subtitle={o.candidate_class === 'gap' ? 'Gap candidate' : undefined}
                metric={o.roi ? formatUsd(o.roi.annual_savings) + '/yr' : undefined}
                active={activeAssetId === id}
                onClick={() => onSelectAsset(id)}
              />
            );
          })}
        </div>
      )}
    </aside>
  );
}
